import React from 'react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { TypePanel } from '@/components/app/panels/left-panel/type-panel/type-panel'
import { useDocumentLayout } from '@/contexts/document-layout-context'
import { useEditorCommandState } from '@/contexts/editor-command-state-context'
import { useEditorCommands } from '@/contexts/editor-commands-context'
import { useEditorTools } from '@/contexts/editor-tools-context'
import { useTextDefaults } from '@/contexts/text-defaults-context'
import type { Paragraph } from '@/api/types/stowed-element'

interface TypeToolProps {
  textPanelRevision?: number
  selectedShapeTextParagraphs?: Paragraph[] | null
  isSelectedShapeTextFrame?: boolean
}

/** The Type tool's panel: the Type section, with its style controls disabled while locked or in node edit. */
export const TypeTool: React.FC<TypeToolProps> = ({
  textPanelRevision = 0,
  selectedShapeTextParagraphs,
  isSelectedShapeTextFrame,
}) => {
  const { spreadId } = useDocumentLayout()
  const { defaultFontFamilyId, defaultFontName, defaultFontSize } = useTextDefaults()
  const { isNodeEditMode } = useEditorTools()
  const { lockedSelectionDisablesPropertyPanels, textStyleDisabled, shapeTextEditRevision } =
    useEditorCommandState()
  const commands = useEditorCommands()
  const styleControlsDisabled =
    lockedSelectionDisablesPropertyPanels || textStyleDisabled || isNodeEditMode

  return (
    <Accordion type="multiple" defaultValue={['type']} className="flex w-full flex-col gap-4">
      <AccordionItem value="type" className="border-none">
        <AccordionTrigger className="py-2 px-2 rounded-md text-sm font-normal leading-none text-foreground bg-muted/70 hover:bg-muted/80 [&>svg]:text-foreground hover:no-underline">
          Type
        </AccordionTrigger>
        <AccordionContent className="pt-3 pb-0">
          <TypePanel
            selectedShapeTextParagraphs={selectedShapeTextParagraphs}
            isSelectedShapeTextFrame={isSelectedShapeTextFrame}
            restoreShapeTextFocus={commands.restoreShapeTextFocus}
            getEditingShapeTextSelectionStyle={commands.getEditingShapeTextSelectionStyle}
            getEditingShapeTextId={commands.getEditingShapeTextId}
            getEditingShapeTextKerningState={commands.getEditingShapeTextKerningState}
            shapeTextEditRevision={shapeTextEditRevision}
            disabled={styleControlsDisabled}
            onStyleUpdate={commands.applyTextStyle}
            applyKerningAtCaret={commands.applyKerningAtCaret}
            spreadId={spreadId}
            textPanelRevision={textPanelRevision}
            defaultFontFamilyId={defaultFontFamilyId}
            defaultFontName={defaultFontName}
            defaultFontSize={defaultFontSize}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
